import { useQuery } from "@tanstack/react-query";
import { projectsAPI, employeesAPI } from "../services/api";
import { useMemo } from "react";

interface UpcomingProjectEnd {
  employee: {
    id: string;
    firstName: string;
    lastName: string;
    user?: {
      profilePicture?: string;
    };
    profilePicture?: string;
  };
  project: {
    id: string;
    name: string;
    client?: {
      name: string;
    };
  };
  role?: string;
  endDate: Date;
  daysUntil: number;
}

export const useUpcomingProjectEnds = (
  daysAhead: number = 30,
  enabled: boolean = true,
) => {
  const { data: projects = [], isLoading: projectsLoading } = useQuery({
    queryKey: ["projects"],
    queryFn: projectsAPI.getAll,
    enabled,
  });

  const { data: employees = [], isLoading: employeesLoading } = useQuery({
    queryKey: ["employees"],
    queryFn: employeesAPI.getAll,
    enabled,
  });

  const upcomingProjectEnds = useMemo(() => {
    if (!projects || projects.length === 0) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + daysAhead);

    const projectEnds: UpcomingProjectEnd[] = [];

    projects.forEach((project: any) => {
      if (!project.employees || project.employees.length === 0) return;

      project.employees.forEach((projectEmployee: any) => {
        // Skip if no endDate or if shadow employee
        if (!projectEmployee.endDate || projectEmployee.shadow) return;

        const employeeEndDate = new Date(projectEmployee.endDate);
        employeeEndDate.setHours(0, 0, 0, 0);

        // Check if end date is within the specified days ahead and in the future
        if (employeeEndDate >= today && employeeEndDate <= endDate) {
          const daysUntil = Math.ceil(
            (employeeEndDate.getTime() - today.getTime()) /
              (1000 * 60 * 60 * 24),
          );

          // Find full employee data for profile picture
          const fullEmployee = employees.find(
            (emp: any) => emp.id === projectEmployee.id,
          );

          // Skip if employee is not active (only if we have employee data)
          if (fullEmployee && !fullEmployee.isActive) return;

          projectEnds.push({
            employee: {
              id: projectEmployee.id,
              firstName: projectEmployee.firstName,
              lastName: projectEmployee.lastName,
              user: fullEmployee?.user,
              profilePicture: fullEmployee?.profilePicture,
            },
            project: {
              id: project.id,
              name: project.name,
              client: project.client,
            },
            role: projectEmployee.role,
            endDate: employeeEndDate,
            daysUntil,
          });
        }
      });
    });

    // Sort by date (soonest first)
    return projectEnds.sort(
      (a, b) => a.endDate.getTime() - b.endDate.getTime(),
    );
  }, [projects, employees, daysAhead]);

  return {
    upcomingProjectEnds,
    isLoading: projectsLoading || employeesLoading,
  };
};
